import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

const Page = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
  h1 { margin: 0; font-size: 1.8rem; }
  button, input, select { font: inherit; min-height: 44px; border-radius: 6px; }
  button { padding: 8px 14px; background: var(--accent); color: white; }
  button.danger { background: #9b2c2c; }
  button:disabled { opacity: 0.55; cursor: default; }
  input, select { padding: 8px 10px; border: 1px solid var(--line-strong); background: var(--surface); color: var(--text); }
  input { width: 100%; min-width: 0; }
`;
const Toolbar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 12px; margin: 18px 0;
  form { display: flex; flex: 1; gap: 8px; max-width: 600px; }
  label { flex: 1; min-width: 0; }
`;
const UserList = styled.ul`
  list-style: none; padding: 0; margin: 0; border-top: 1px solid var(--line-strong);
`;
const UserRow = styled.li`
  display: grid; grid-template-columns: minmax(0, 1fr) 120px minmax(280px, 340px) 145px;
  align-items: center; gap: 16px; padding: 18px 0; border-bottom: 1px solid var(--line);
  .email { overflow-wrap: anywhere; font-weight: 600; }
  .status { color: var(--muted); font-size: 0.9rem; }
  form { display: flex; align-items: end; gap: 8px; }
  label { flex: 1; min-width: 0; font-size: 0.85rem; }
  select { display: block; width: 100%; margin-top: 4px; }
  @media (max-width: 750px) {
    grid-template-columns: minmax(0, 1fr); gap: 8px;
    form { max-width: 400px; }
  }
`;

function UsersPage() {
  const { user: currentUser } = useAuth();
  const [data, setData] = useState({ users: [], roles: [], total: 0, pageSize: 50 });
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [drafts, setDrafts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError('');
    api.getUsers({ q: query, page }).then((response) => {
      if (!active) return;
      setData(response);
      setDrafts({});
    }).catch((failure) => {
      if (active) {
        setError(failure.message);
        setData({ users: [], roles: [], total: 0, pageSize: 50 });
      }
    }).finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [query, page, reload]);

  const saveRole = async (event, user) => {
    event.preventDefault();
    setPendingAction(`role:${user.userId}`);
    setError('');
    setNotice('');
    try {
      const response = await api.updateUserRole(user.userId, drafts[user.userId], user.role);
      setData((current) => ({ ...current, users: current.users.map((entry) =>
        entry.userId === user.userId ? response.user : entry) }));
      setDrafts((current) => ({ ...current, [user.userId]: response.user.role }));
      setNotice(`${response.user.email} is now a ${response.user.role}.`);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setPendingAction('');
    }
  };

  const changeActive = async (user) => {
    const active = !user.active;
    if (!active && !window.confirm(
      `Deactivate ${user.email}? They will be signed out on their next request and cannot sign in.`
    )) return;
    setPendingAction(`active:${user.userId}`);
    setError('');
    setNotice('');
    try {
      const response = await api.updateUserActive(user.userId, active, user.active);
      setData((current) => ({ ...current, users: current.users.map((entry) =>
        entry.userId === user.userId ? response.user : entry) }));
      setNotice(`${response.user.email} has been ${active ? 'reactivated' : 'deactivated'}.`);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setPendingAction('');
    }
  };

  const busy = isLoading || Boolean(pendingAction);
  const pageCount = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <Page>
      <h1>Users</h1>
      <Toolbar>
        <form onSubmit={(event) => {
          event.preventDefault(); setQuery(search.trim()); setPage(1); setReload((value) => value + 1); setNotice('');
        }}>
          <label>
            <input type="search" aria-label="Search users by email" placeholder="Search email"
              value={search} onChange={(event) => setSearch(event.target.value)} disabled={busy} />
          </label>
          <button type="submit" disabled={busy}>Search</button>
        </form>
        <button type="button" disabled={busy} onClick={() => {
          setReload((value) => value + 1); setNotice('');
        }}>Reload Users</button>
      </Toolbar>
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {isLoading ? <p role="status">Loading users...</p> : (
        <>
          <p>{data.total} {data.total === 1 ? 'user' : 'users'}</p>
          <UserList aria-label="Users">
            {data.users.map((user) => {
              const role = drafts[user.userId] || user.role;
              const isSelf = user.userId === currentUser?.userId;
              return (
                <UserRow key={user.userId}>
                  <div className="email">{user.email}{isSelf ? ' (you)' : ''}</div>
                  <div className="status">{!user.active ? 'Deactivated' : user.emailVerified ? 'Active' : 'Pending email'}</div>
                  <form onSubmit={(event) => saveRole(event, user)}>
                    <label>
                      Role
                      <select aria-label={`Role for ${user.email}`} value={role}
                        disabled={busy || isSelf}
                        title={isSelf ? 'Your own System Admin role cannot be changed here.' : undefined}
                        onChange={(event) => setDrafts((current) => ({ ...current, [user.userId]: event.target.value }))}>
                        {data.roles.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                    <button type="submit" disabled={busy || isSelf || role === user.role}>
                      {pendingAction === `role:${user.userId}` ? 'Saving...' : 'Save'}
                    </button>
                  </form>
                  <button type="button" className={user.active ? 'danger' : undefined}
                    disabled={busy || isSelf}
                    title={isSelf ? 'You cannot deactivate your own account.' : undefined}
                    onClick={() => changeActive(user)}>
                    {pendingAction === `active:${user.userId}`
                      ? user.active ? 'Deactivating...' : 'Reactivating...'
                      : user.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </UserRow>
              );
            })}
          </UserList>
          {!error && !data.users.length ? <p>No users found.</p> : null}
        </>
      )}
      <Toolbar>
        <button type="button" disabled={busy || page === 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
        <span>Page {page} of {pageCount}</span>
        <button type="button" disabled={busy || page >= pageCount} onClick={() => setPage((value) => value + 1)}>Next</button>
      </Toolbar>
    </Page>
  );
}

export default UsersPage;
